import { resolveSchema, resolveDomainEvent } from './workflowSpec.js'
import { computeMidString, getLastCardSortkey } from './sortkey.js'
import {
  buildSchemaField,
  resolveEntityRef,
  resolveFieldRefs,
  resolveFieldUpdateRefs,
  applyNestedFieldUpdates,
  buildSchemaRef
} from './schemaFields.js'
import { sanitizeCardDescription } from './sanitize.js'
import { DomainModelRole } from '../constants/cardTypes.js'
import { SchemaType } from '../constants/schema.js'

const generateId = () => globalThis.crypto.randomUUID()

const findDomainEventCardBySchemaId = (domainEvents, schemaId, cardTypesMap) => {
  for (let eventIndex = 0; eventIndex < domainEvents.length; eventIndex++) {
    const event = domainEvents[eventIndex]
    const cards = event.cards || []
    for (let cardIndex = 0; cardIndex < cards.length; cardIndex++) {
      const card = cards[cardIndex]
      const cardType = cardTypesMap[card.cardTypeId]
      if (cardType?.domainModelRole === DomainModelRole.DOMAIN_EVENT && card.schemaId === schemaId) {
        return { card, event, eventIndex, cardIndex }
      }
    }
  }

  return null
}

export const buildCreateDomainEventSchemasMutation = ({ workflow, workflowId, domainEventSchemas, cardTypes }) => {
  if (!Array.isArray(domainEventSchemas) || domainEventSchemas.length === 0) {
    return { error: 'domainEventSchemas is required and must be a non-empty array' }
  }

  const cardTypesList = cardTypes || []
  const domainEventCardType = cardTypesList.find((cardType) => cardType.domainModelRole === DomainModelRole.DOMAIN_EVENT)
  if (!domainEventCardType) {
    return { error: 'Domain Event card type not found in this workflow' }
  }
  const cardTypesMap = Object.fromEntries(cardTypesList.map((cardType) => [cardType.id, cardType]))

  const existingSchemaNames = new Set(
    (workflow.schemas || [])
      .filter((schema) => schema.type === SchemaType.DOMAIN_EVENT)
      .map((schema) => schema.name)
  )
  const batchNames = new Set()
  const batchEventIds = new Set()

  const domainEvents = workflow.domainEvents || []
  let lastSortkey = getLastCardSortkey(domainEvents)

  const newSchemas = []
  const pendingNewCards = []
  const pendingCardLinks = []

  for (const entry of domainEventSchemas) {
    const { domainEvent, name, entity, fields } = entry || {}

    if (!domainEvent) {
      return { error: 'Each domain event schema must have a domainEvent ref' }
    }
    if (!name || typeof name !== 'string' || name.trim() === '') {
      return { error: 'Each domain event schema must have a non-empty name' }
    }

    const sanitizedName = sanitizeCardDescription(name)

    if (batchNames.has(sanitizedName)) {
      return { error: `Duplicate domain event schema name "${sanitizedName}" in the batch` }
    }
    batchNames.add(sanitizedName)

    if (existingSchemaNames.has(sanitizedName)) {
      return { error: `Domain event schema "${sanitizedName}" already exists` }
    }

    const matchedEvent = resolveDomainEvent(domainEvent, workflow)
    if (!matchedEvent) {
      return { error: `Domain event not found for ref: ${domainEvent}` }
    }

    if (batchEventIds.has(matchedEvent.id)) {
      return { error: `Multiple domain event schemas target the same domain event "${matchedEvent.description}" in the batch` }
    }
    batchEventIds.add(matchedEvent.id)

    const existingDomainEventCard = (matchedEvent.cards || []).find((card) => {
      return cardTypesMap[card.cardTypeId]?.domainModelRole === DomainModelRole.DOMAIN_EVENT
    })
    if (existingDomainEventCard?.schemaId) {
      return { error: `Event "${matchedEvent.description}" already has a Domain Event schema. Use update_domain_event_schema to modify it.` }
    }

    const entityId = entity ? resolveEntityRef(entity, workflow) : null
    const resolvedFields = resolveFieldRefs(fields, workflow)
    const newSchema = {
      id: generateId(),
      name: sanitizedName,
      type: SchemaType.DOMAIN_EVENT,
      entityId: entityId || null,
      fields: (resolvedFields || []).map((field) => buildSchemaField(field, SchemaType.DOMAIN_EVENT))
    }
    newSchemas.push({ ...newSchema, index: -1 })

    if (existingDomainEventCard) {
      // The event already has a Domain Event card with no schema — link it to the new schema.
      pendingCardLinks.push({
        eventId: matchedEvent.id,
        card: existingDomainEventCard,
        cardIndex: (matchedEvent.cards || []).indexOf(existingDomainEventCard),
        schemaId: newSchema.id
      })
    } else {
      const sortkey = computeMidString(lastSortkey, '')
      lastSortkey = sortkey

      pendingNewCards.push({
        eventId: matchedEvent.id,
        name,
        schemaId: newSchema.id,
        sortkey
      })
    }
  }

  const schemaInput = {
    id: workflowId,
    expectedVersion: workflow.version,
    schemas: newSchemas,
    updateNote: {
      action: 'created domain event schemas',
      target: newSchemas.map((schema) => schema.name).join(', ')
    }
  }

  const cards = []

  for (const { eventId, name, schemaId, sortkey } of pendingNewCards) {
    const eventIndex = domainEvents.findIndex((event) => event.id === eventId)
    cards.push({
      description: name,
      done: false,
      cardTypeId: domainEventCardType.id,
      releaseNo: null,
      sortkey,
      boundedContext: null,
      schemaId,
      cardIndex: -1,
      eventId,
      eventIndex
    })
  }

  for (const { eventId, card, cardIndex, schemaId } of pendingCardLinks) {
    const eventIndex = domainEvents.findIndex((event) => event.id === eventId)
    cards.push({
      cardId: card.id,
      cardIndex,
      expectedVersion: card.version,
      description: card.description,
      sortkey: card.sortkey,
      cardTypeId: card.cardTypeId,
      done: card.done ?? false,
      schemaId,
      boundedContext: card.boundedContext,
      cardinality: card.cardinality,
      releaseNo: card.releaseNo,
      eventId,
      eventIndex
    })
  }

  // cardInput's expectedVersion is threaded in by the caller after schemaInput persists.
  const cardInput = cards.length > 0
    ? {
        workflowId,
        updateNote: {
          action: 'added domain event cards',
          target: newSchemas.map((schema) => schema.name).join(', ')
        },
        cards
      }
    : null

  return {
    schemaInput,
    cardInput,
    newSchemas,
    refs: newSchemas.map((schema) => ({ name: schema.name, ref: buildSchemaRef('domainEvents', schema.name) }))
  }
}

export const buildUpdateDomainEventSchemasMutation = ({ workflow, workflowId, domainEventSchemas, cardTypes }) => {
  if (!Array.isArray(domainEventSchemas) || domainEventSchemas.length === 0) {
    return { error: 'domainEventSchemas is required and must be a non-empty array' }
  }

  const cardTypesMap = Object.fromEntries((cardTypes || []).map((cardType) => [cardType.id, cardType]))

  const claimedSchemaIds = new Set()
  const claimedNewNames = new Set()
  const schemaInputs = []
  const changeSummaries = []
  const refsOut = []
  const pendingRenames = []

  for (const update of domainEventSchemas) {
    const {
      domainEventSchema: schemaRef,
      name,
      entity,
      addFields: rawAdds,
      updateFields: rawUpdates,
      removeFields
    } = update || {}

    if (!schemaRef) {
      return { error: 'Each item must include a domainEventSchema ref' }
    }

    const resolved = resolveSchema(schemaRef, workflow, cardTypesMap)
    if (!resolved) {
      return { error: `Domain event schema not found for ref: ${schemaRef}` }
    }
    const schemaId = resolved.id

    if (claimedSchemaIds.has(schemaId)) {
      return { error: `Domain event schema "${schemaRef}" appears more than once in the batch` }
    }
    claimedSchemaIds.add(schemaId)

    const schemaIndex = (workflow.schemas || []).findIndex((schema) => schema.id === schemaId)
    if (schemaIndex === -1) {
      return { error: `Domain event schema not found for ref: ${schemaRef}` }
    }

    const addFields = resolveFieldRefs(rawAdds, workflow)
    const updateFields = resolveFieldUpdateRefs(rawUpdates, workflow)

    const schema = workflow.schemas[schemaIndex]
    const currentFields = [...(schema.fields || [])]
    const changes = []

    if (removeFields && removeFields.length > 0) {
      for (const fieldName of removeFields) {
        const fieldIndex = currentFields.findIndex((field) => field.name === fieldName)
        if (fieldIndex === -1) {
          return { error: `Field "${fieldName}" not found in domain event schema "${schema.name}"` }
        }
        currentFields.splice(fieldIndex, 1)
        changes.push(`removed "${fieldName}"`)
      }
    }

    if (updateFields && updateFields.length > 0) {
      for (const { fieldName, updates } of updateFields) {
        const fieldIndex = currentFields.findIndex((field) => field.name === fieldName)
        if (fieldIndex === -1) {
          return { error: `Field "${fieldName}" not found in domain event schema "${schema.name}"` }
        }

        if (updates.name && updates.name !== fieldName) {
          const collision = currentFields.find((field) => field.name === updates.name)
          if (collision) {
            return { error: `Field "${updates.name}" already exists in domain event schema "${schema.name}"` }
          }
        }

        const updatedField = { ...currentFields[fieldIndex] }

        if (updates.name !== undefined) {
          updatedField.name = updates.name
        }
        if (updates.relatedEntityId !== undefined) {
          updatedField.relatedEntityId = updates.relatedEntityId
          if (!updatedField.relatedEntityId) {
            delete updatedField.fields
          }
        }
        if (updates.cardinality !== undefined) {
          updatedField.cardinality = updates.cardinality
        }
        if (updates.fields !== undefined) {
          const nested = applyNestedFieldUpdates(updatedField.fields, updates.fields)
          if (nested && nested.length > 0) {
            updatedField.fields = nested
          } else {
            delete updatedField.fields
          }
        }

        currentFields[fieldIndex] = updatedField
        changes.push(`updated "${fieldName}"`)
      }
    }

    if (addFields && addFields.length > 0) {
      for (const field of addFields) {
        if (currentFields.find((existing) => existing.name === field.name)) {
          return { error: `Field "${field.name}" already exists in domain event schema "${schema.name}"` }
        }
        currentFields.push(buildSchemaField(field, SchemaType.DOMAIN_EVENT))
        changes.push(`added "${field.name}"`)
      }
    }

    const updatedSchema = {
      ...schema,
      fields: currentFields
    }

    const sanitizedName = name !== undefined ? sanitizeCardDescription(name) : undefined
    if (sanitizedName !== undefined && sanitizedName !== schema.name) {
      const collision = workflow.schemas.find((other) => other.name === sanitizedName && other.type === SchemaType.DOMAIN_EVENT && other.id !== schemaId)
      if (collision) {
        return { error: `Domain event schema "${sanitizedName}" already exists` }
      }
      if (claimedNewNames.has(sanitizedName)) {
        return { error: `Domain event schema "${sanitizedName}" is set by more than one entry in the batch` }
      }
      claimedNewNames.add(sanitizedName)
      updatedSchema.name = sanitizedName
      changes.push(`renamed to "${sanitizedName}"`)
      pendingRenames.push({ schemaId, newName: sanitizedName })
    }

    if (entity !== undefined) {
      updatedSchema.entityId = entity ? resolveEntityRef(entity, workflow) : null
      changes.push('entity updated')
    }

    if (changes.length === 0) {
      return { error: `No changes specified for "${schema.name}"` }
    }

    schemaInputs.push({
      ...updatedSchema,
      index: schemaIndex,
      expectedVersion: schema.version || null
    })
    changeSummaries.push(`"${schema.name}": ${changes.join(', ')}`)
    refsOut.push({
      name: updatedSchema.name,
      ref: buildSchemaRef('domainEvents', updatedSchema.name)
    })
  }

  const schemaInput = {
    id: workflowId,
    expectedVersion: workflow.version,
    schemas: schemaInputs,
    updateNote: {
      action: 'updated domain event schemas',
      target: schemaInputs.map((schema) => schema.name).join(', ')
    }
  }

  // Renaming a domain event schema also renames its linked Domain Event card.
  // cardInput's expectedVersion is threaded in by the caller after schemaInput persists.
  let cardInput = null

  if (pendingRenames.length > 0) {
    const cardsToUpdate = []

    for (const { schemaId, newName } of pendingRenames) {
      const match = findDomainEventCardBySchemaId(workflow.domainEvents || [], schemaId, cardTypesMap)
      if (!match) {
        continue
      }

      cardsToUpdate.push({
        cardId: match.card.id,
        cardIndex: match.cardIndex,
        expectedVersion: match.card.version,
        description: newName,
        sortkey: match.card.sortkey,
        cardTypeId: match.card.cardTypeId,
        done: match.card.done ?? false,
        schemaId: match.card.schemaId,
        boundedContext: match.card.boundedContext,
        releaseNo: match.card.releaseNo,
        eventId: match.event.id,
        eventIndex: match.eventIndex
      })
    }

    if (cardsToUpdate.length > 0) {
      cardInput = {
        workflowId,
        updateNote: {
          action: 'renamed domain event cards',
          target: pendingRenames.map((rename) => rename.newName).join(', ')
        },
        cards: cardsToUpdate
      }
    }
  }

  return { schemaInput, cardInput, changeSummaries, refs: refsOut }
}

export const buildDeleteDomainEventSchemaMutation = ({ workflow, workflowId, domainEventSchema, cardTypes }) => {
  if (!domainEventSchema) {
    return { error: 'domainEventSchema ref is required' }
  }

  const cardTypesMap = Object.fromEntries((cardTypes || []).map((cardType) => [cardType.id, cardType]))

  const resolved = resolveSchema(domainEventSchema, workflow, cardTypesMap)
  if (!resolved) {
    return { error: `Domain event schema not found for ref: ${domainEventSchema}` }
  }
  const schemaId = resolved.id

  const schemaIndex = (workflow.schemas || []).findIndex((schema) => schema.id === schemaId)
  if (schemaIndex === -1) {
    return { error: 'Domain event schema not found' }
  }

  const schema = workflow.schemas[schemaIndex]
  const match = findDomainEventCardBySchemaId(workflow.domainEvents || [], schemaId, cardTypesMap)

  // The linked Domain Event card (if any) is deleted first; both steps' expectedVersion is threaded by the caller.
  const cardDelete = match
    ? {
        workflowId,
        eventId: match.event.id,
        eventIndex: match.eventIndex,
        cardId: match.card.id,
        cardIndex: match.cardIndex,
        cardDescription: match.card.description
      }
    : null

  const schemaInput = {
    id: workflowId,
    deletedSchemas: [{ deleteIndex: schemaIndex, deleteId: schemaId }],
    updateNote: {
      action: 'deleted domain event schema',
      target: schema.name
    }
  }

  return { cardDelete, schemaInput, schema }
}
